import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ConnectivityService } from '@app/core/services/internal/connectivity.service';
import { NgIf } from '@angular/common';

const STORAGE_KEY = 'lumivraz.offlineBubble.position';
const BUBBLE_SIZE = 52;
const EDGE_MARGIN = 12;
const DRAG_THRESHOLD_PX = 4;

@Component({
    selector: 'app-offline-bubble',
    templateUrl: './offline-bubble.component.html',
    styleUrls: ['./offline-bubble.component.scss'],
    imports: [NgIf],
})
export class OfflineBubbleComponent implements OnInit, OnDestroy {
  @ViewChild('bubble', { static: false }) bubbleRef?: ElementRef<HTMLElement>;

  online = true;
  expanded = false;
  posX = EDGE_MARGIN;
  posY = EDGE_MARGIN;

  private subscription?: Subscription;
  private dragging = false;
  private moved = false;
  private startPointerX = 0;
  private startPointerY = 0;
  private startBubbleX = 0;
  private startBubbleY = 0;
  private activePointerId: number | null = null;

  constructor(readonly connectivity: ConnectivityService) {}

  ngOnInit() {
    this.restorePosition();
    this.online = this.connectivity.isOnline;
    this.subscription = this.connectivity.isOnline$.subscribe((value) => {
      this.online = value;
      if (value) this.expanded = false;
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  onPointerDown(event: PointerEvent) {
    if (event.button !== undefined && event.button !== 0) return;
    this.dragging = true;
    this.moved = false;
    this.startPointerX = event.clientX;
    this.startPointerY = event.clientY;
    this.startBubbleX = this.posX;
    this.startBubbleY = this.posY;
    this.activePointerId = event.pointerId;
    (event.target as Element)?.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (!this.dragging || event.pointerId !== this.activePointerId) return;
    const dx = event.clientX - this.startPointerX;
    const dy = event.clientY - this.startPointerY;
    if (!this.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    this.moved = true;
    this.posX = this.clampX(this.startBubbleX + dx);
    this.posY = this.clampY(this.startBubbleY + dy);
  }

  @HostListener('window:pointerup', ['$event'])
  onPointerUp(event: PointerEvent) {
    if (event.pointerId !== this.activePointerId) return;
    const wasDragging = this.dragging;
    const wasMoved = this.moved;
    this.dragging = false;
    this.activePointerId = null;
    if (wasDragging && wasMoved) {
      this.snapToEdge();
      this.persistPosition();
    } else if (wasDragging && !wasMoved) {
      this.expanded = !this.expanded;
    }
    this.moved = false;
  }

  @HostListener('window:resize')
  onResize() {
    this.posX = this.clampX(this.posX);
    this.posY = this.clampY(this.posY);
  }

  private clampX(value: number): number {
    const maxX = window.innerWidth - BUBBLE_SIZE - EDGE_MARGIN;
    return Math.max(EDGE_MARGIN, Math.min(maxX, value));
  }

  private clampY(value: number): number {
    const maxY = window.innerHeight - BUBBLE_SIZE - EDGE_MARGIN;
    return Math.max(EDGE_MARGIN, Math.min(maxY, value));
  }

  private snapToEdge() {
    const halfX = window.innerWidth / 2;
    this.posX =
      this.posX + BUBBLE_SIZE / 2 < halfX
        ? EDGE_MARGIN
        : window.innerWidth - BUBBLE_SIZE - EDGE_MARGIN;
  }

  private restorePosition() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { x?: number; y?: number };
      if (typeof parsed?.x === 'number') this.posX = this.clampX(parsed.x);
      if (typeof parsed?.y === 'number') this.posY = this.clampY(parsed.y);
    } catch {

    }
  }

  private persistPosition() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ x: this.posX, y: this.posY }),
      );
    } catch {

    }
  }
}
